import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Plus, CircleAlert, Receipt } from "lucide-react";
import { Pagina, Cartao, Carregando, Falha } from "../shell/Pagina.js";
import { EmptyState } from "@pessoas/ui";
import { trpc } from "../lib/trpc.js";
import { data as fData, moeda } from "../lib/formato.js";
import { usePode } from "../shell/Sessao.js";

/**
 * Naturezas do S-1010 mais usadas.
 *
 * São ELAS que determinam as incidências. O usuário escolhe a natureza; o
 * resto é consequência de uma tabela de mapeamento versionada. Sistema com
 * conceito próprio de incidência preenchido à mão calcula de um jeito e
 * transmite de outro, e a divergência só aparece na DCTFWeb meses depois.
 */
const NATUREZAS: { codigo: string; nome: string;
                   inss: boolean; irrf: boolean; fgts: boolean; dsr: boolean }[] = [
  { codigo: "1000", nome: "1000 · Salário, vencimento, soldo", inss: true, irrf: true, fgts: true, dsr: true },
  { codigo: "1002", nome: "1002 · Hora extra", inss: true, irrf: true, fgts: true, dsr: true },
  { codigo: "1003", nome: "1003 · Adicional noturno", inss: true, irrf: true, fgts: true, dsr: true },
  { codigo: "1005", nome: "1005 · Adicional de insalubridade", inss: true, irrf: true, fgts: true, dsr: true },
  { codigo: "1006", nome: "1006 · Adicional de periculosidade", inss: true, irrf: true, fgts: true, dsr: true },
  { codigo: "1020", nome: "1020 · Descanso semanal remunerado", inss: true, irrf: true, fgts: true, dsr: false },
  { codigo: "1201", nome: "1201 · Férias gozadas", inss: true, irrf: true, fgts: true, dsr: false },
  { codigo: "1350", nome: "1350 · 13º salário", inss: true, irrf: true, fgts: true, dsr: false },
  { codigo: "5502", nome: "5502 · Diárias de viagem", inss: false, irrf: false, fgts: false, dsr: false },
  { codigo: "9200", nome: "9200 · Desconto de adiantamento", inss: false, irrf: false, fgts: false, dsr: false },
  { codigo: "9201", nome: "9201 · Contribuição previdenciária do segurado", inss: false, irrf: false, fgts: false, dsr: false },
  { codigo: "9203", nome: "9203 · IRRF", inss: false, irrf: false, fgts: false, dsr: false },
  { codigo: "9219", nome: "9219 · Vale-transporte, parte do trabalhador", inss: false, irrf: false, fgts: false, dsr: false },
  { codigo: "9220", nome: "9220 · Vale-refeição, parte do trabalhador", inss: false, irrf: false, fgts: false, dsr: false },
];

const TIPO: Record<string, string> = {
  provento: "Provento", desconto: "Desconto",
  informativa: "Informativa", base: "Base",
};

export function Rubricas() {
  const [criando, setCriando] = useState(false);
  const podeEscrever = usePode("folha.calcular");
  const qc = useQueryClient();

  const q = useQuery({
    queryKey: ["folha", "rubricas"],
    queryFn: () => trpc.fechamento.rubricas.query({}),
  });

  const padrao = useMutation({
    mutationFn: () => trpc.fechamento.criarRubricasPadrao.mutate({
      vigenciaInicio: `${new Date().getFullYear()}-01-01`,
    }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["folha"] }),
  });

  return (
    <Pagina
      secao="Folha" titulo="Rubricas"
      descricao="A rubrica vale por competência, não por data de hoje: ela precisa estar vigente no período de apuração, senão o eSocial rejeita o evento."
      acoes={podeEscrever ? (
        <div className="flex items-center gap-2">
          {(q.data?.length ?? 0) === 0 && (
            <button className="botao" disabled={padrao.isPending}
                    onClick={() => padrao.mutate()}>
              {padrao.isPending ? "Criando" : "Criar as básicas"}
            </button>
          )}
          <button className="botao" data-variante="primario" onClick={() => setCriando(true)}>
            <Plus aria-hidden size={15} /> Nova rubrica
          </button>
        </div>
      ) : undefined}
    >
      {q.isPending && <Carregando linhas={5} />}
      {q.isError && <Falha erro={q.error} tentarDeNovo={() => void q.refetch()} />}
      {q.isSuccess && q.data.length === 0 && (
        <EmptyState
          titulo="Nenhuma rubrica cadastrada"
          descricao="Sem rubrica vigente na competência, a folha não tem o que calcular. Comece pelo salário base."
          {...(podeEscrever
            ? { acao: (
                <button className="botao" data-variante="primario"
                        disabled={padrao.isPending} onClick={() => padrao.mutate()}>
                  {/* Salário, INSS e IRRF. Os códigos 9201 e 9203 não são
                      escolha: é o que o motor emite. */}
                  {padrao.isPending ? "Criando" : "Criar salário, INSS e IRRF"}
                </button>
              ) }
            : {})}
        />
      )}
      {q.isSuccess && q.data.length > 0 && (
        <Cartao titulo="Cadastradas" subtitulo={`${q.data.length} no total, incluindo versões vencidas`}>
          <ul className="eop-rubricas">
            {q.data.map((r) => (
              <li key={r.id}>
                <span aria-hidden className="eop-rubrica-icone" data-tipo={r.tipo}>
                  <Receipt size={14} />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="eop-rubrica-nome">
                    <span className="num">{r.codigo}</span> {r.descricao}
                  </span>
                  <span className="eop-rubrica-sub">
                    {TIPO[r.tipo]} · natureza {r.naturezaESocial} · vigente de{" "}
                    {fData(r.vigenciaInicio)}
                    {r.vigenciaFim ? ` a ${fData(r.vigenciaFim)}` : " em diante"}
                  </span>
                </span>
                <span className="eop-rubrica-incidencias">
                  {r.incideInss && <span className="pilula">INSS</span>}
                  {r.incideIrrf && <span className="pilula">IRRF</span>}
                  {r.incideFgts && <span className="pilula">FGTS</span>}
                </span>
                {r.valorFixo && <span className="num eop-rubrica-valor">{moeda(r.valorFixo)}</span>}
              </li>
            ))}
          </ul>
        </Cartao>
      )}

      {criando && (
        <FormRubrica aoFechar={() => setCriando(false)}
                     aoCriar={() => { setCriando(false);
                       void qc.invalidateQueries({ queryKey: ["folha"] }); }} />
      )}
    </Pagina>
  );
}

function FormRubrica({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: () => void }) {
  const [f, setF] = useState({
    codigo: "1000", descricao: "Salário base", tipo: "provento",
    natureza: "1000", vigenciaInicio: `${new Date().getFullYear()}-01-01`,
  });
  const [erro, setErro] = useState<string | null>(null);
  const nat = NATUREZAS.find((n) => n.codigo === f.natureza)!;

  const criar = useMutation({
    mutationFn: () => trpc.fechamento.criarRubrica.mutate({
      codigo: f.codigo, descricao: f.descricao,
      tipo: f.tipo as "provento", naturezaESocial: f.natureza,
      vigenciaInicio: f.vigenciaInicio, vigenciaFim: null, valorFixo: null,
      // Derivadas da natureza, nunca digitadas.
      incideInss: nat.inss, incideIrrf: nat.irrf, incideFgts: nat.fgts, incideDsr: nat.dsr,
    }),
    onSuccess: aoCriar,
    onError: (e: unknown) => setErro(e instanceof Error ? e.message : String(e)),
  });

  return (
    <div className="eop-modal-fundo" role="dialog" aria-modal="true" aria-label="Nova rubrica"
         onMouseDown={(e) => e.target === e.currentTarget && aoFechar()}>
      <div className="eop-modal" style={{ maxWidth: "34rem" }}>
        <header className="painel-topo">
          <h2 className="text-[0.9375rem] font-semibold">Nova rubrica</h2>
          <p className="mt-1 text-xs" style={{ color: "var(--faint-foreground)" }}>
            As incidências vêm da natureza do S-1010 e não são editáveis: digitar
            incidência à mão faz o sistema calcular de um jeito e transmitir de outro.
          </p>
        </header>
        <form className="painel-corpo space-y-3.5"
              onSubmit={(e) => { e.preventDefault(); setErro(null); criar.mutate(); }}>
          <div className="grid gap-3.5 sm:grid-cols-2">
            <label className="block">
              <span className="eop-rotulo">Código</span>
              <input className="campo mt-1.5 num" required value={f.codigo}
                     onChange={(e) => setF((v) => ({ ...v, codigo: e.target.value }))} />
            </label>
            <label className="block">
              <span className="eop-rotulo">Tipo</span>
              <select className="campo mt-1.5" value={f.tipo}
                      onChange={(e) => setF((v) => ({ ...v, tipo: e.target.value }))}>
                <option value="provento">Provento</option>
                <option value="desconto">Desconto</option>
                <option value="informativa">Informativa</option>
                <option value="base">Base</option>
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Descrição</span>
              <input className="campo mt-1.5" required value={f.descricao}
                     onChange={(e) => setF((v) => ({ ...v, descricao: e.target.value }))} />
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Natureza do eSocial (tabela 3)</span>
              <select className="campo mt-1.5" value={f.natureza}
                      onChange={(e) => setF((v) => ({ ...v, natureza: e.target.value }))}>
                {NATUREZAS.map((n) => <option key={n.codigo} value={n.codigo}>{n.nome}</option>)}
              </select>
            </label>
            <label className="block sm:col-span-2">
              <span className="eop-rotulo">Vigente a partir de</span>
              <input className="campo mt-1.5" type="date" required value={f.vigenciaInicio}
                     onChange={(e) => setF((v) => ({ ...v, vigenciaInicio: e.target.value }))} />
            </label>
          </div>

          <div className="eop-derivadas">
            <span className="eyebrow">Incidências, derivadas da natureza</span>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {nat.inss && <span className="pilula">INSS</span>}
              {nat.irrf && <span className="pilula">IRRF</span>}
              {nat.fgts && <span className="pilula">FGTS</span>}
              {nat.dsr && <span className="pilula">base do DSR</span>}
              {!nat.inss && !nat.irrf && !nat.fgts && (
                <span className="pilula">não integra base nenhuma</span>
              )}
            </div>
          </div>

          {erro && (
            <p className="eop-erro" role="alert">
              <CircleAlert aria-hidden size={15} /><span>{erro}</span>
            </p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" className="botao" data-variante="silencioso" onClick={aoFechar}>
              Cancelar
            </button>
            <button type="submit" className="botao" data-variante="primario" disabled={criar.isPending}>
              {criar.isPending ? "Salvando" : "Cadastrar"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
